import React from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { estadoLicenca, diasRestantes as calcularDiasRestantes, assinaturaVencida } from '../../utils/statusLicenca';

const formatarPreco = (preco) => {
  // preco é anulável no schema, e o plano de avaliação vale 0. A versão anterior
  // fazia plans?.price.toFixed(2): com plans presente e price nulo, o .toFixed
  // estourava e derrubava a renderização do card inteiro.
  if (preco === null || preco === undefined) return 'Não disponível';
  if (Number(preco) === 0) return 'Grátis';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(preco);
};

const SubscriptionCard = ({ subscription }) => {
  const navigate = useNavigate();

  // /plans renderizava so o PricingSection, fora do Layout (sem Header/Footer)
  // — ficava com cara de pagina quebrada. Manda pra home, na secao de planos.
  const irParaPlanos = () => navigate('/#planos');

  if (!subscription) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-rn-navy mb-4">Sua Assinatura</h2>
        <p className="text-rn-text-soft mb-6">
          Você ainda não possui uma assinatura ativa.
        </p>
        <Button variant="primary" className="w-full" onClick={irParaPlanos}>
          Ver planos
        </Button>
      </Card>
    );
  }

  const estado = estadoLicenca(subscription);

  const fim = subscription.current_period_end
    ? new Date(subscription.current_period_end)
    : null;
  const diasRestantes = calcularDiasRestantes(subscription);
  const vencida = assinaturaVencida(subscription);

  // created_at e o momento em que a licenca passou a valer (trial concedido ou
  // compra registrada). Mostrar junto com o fim deixa claro o prazo que correu.
  const inicio = subscription.created_at
    ? new Date(subscription.created_at)
    : null;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-rn-navy">Sua Assinatura</h2>
        <span
          className={`px-3 py-1 rounded-full text-sm font-medium ${estado.fundo} ${estado.cor}`}
        >
          {estado.texto}
        </span>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <h3 className="text-sm font-medium text-rn-text-soft mb-1">
            Plano atual
          </h3>
          <p className="text-rn-navy font-medium">
            {subscription.plans?.name ?? 'Não disponível'}
          </p>
        </div>

        {inicio && (
          <div>
            <h3 className="text-sm font-medium text-rn-text-soft mb-1">
              Ativada em
            </h3>
            <p className="text-rn-navy">{inicio.toLocaleDateString('pt-BR')}</p>
          </div>
        )}

        {fim && (
          <div>
            <h3 className="text-sm font-medium text-rn-text-soft mb-1">
              {vencida ? 'Expirou em' : 'Válida até'}
            </h3>
            <p className="text-rn-navy">
              {fim.toLocaleDateString('pt-BR')}
              {!vencida && diasRestantes !== null && (
                <span className="text-rn-text-soft">
                  {' '}
                  — {diasRestantes} dia{diasRestantes === 1 ? '' : 's'} restante
                  {diasRestantes === 1 ? '' : 's'}
                </span>
              )}
            </p>
          </div>
        )}

        <div>
          <h3 className="text-sm font-medium text-rn-text-soft mb-1">Valor</h3>
          <p className="text-rn-navy">
            {formatarPreco(subscription.plans?.price)}
          </p>
        </div>
      </div>

      {/* O botão "Gerenciar Assinatura" foi removido: não tinha onClick, então
          não fazia absolutamente nada ao ser clicado. Volta quando existir
          checkout — ver PAGAMENTO_DISPONIVEL em PricingSection. */}
      <Button variant="secondary" className="w-full" onClick={irParaPlanos}>
        Ver planos
      </Button>
    </Card>
  );
};

export default SubscriptionCard;
